/* Created by Interactive 63 - https://interactive63.com */
/**
 * The track playback popover: turn repeating on or off, choose how many times and how long each
 * cycle is, play cycles forwards then backwards, and mark the track as playing once.
 */
import type { TrackRepeat } from '../../../core/model/types';
import { repeatPeriod } from '../../../core/timeline/repeat';
import { setTrackPlayback } from '../../store/timeline-actions';
import type { Store } from '../../store/store';
import { formatTime, parseTime } from '../../time-format';
import { h } from '../../ui/dom';

/** Opens the playback popover for tracks, near a screen position. */
export function openPlaybackPopover(
  store: Store,
  trackIds: readonly string[],
  position: { x: number; y: number },
): void {
  const tracks = store.getState().project.tracks.filter((t) => trackIds.includes(t.id));
  const first = tracks[0];
  if (!first) return;

  const current: TrackRepeat = first.repeat ?? {
    period: null,
    count: 'infinite',
    alternate: false,
  };
  const repeating = h('input', { type: 'checkbox', checked: first.repeat !== undefined });
  const count = h('input', {
    type: 'number',
    min: 2,
    max: 1000,
    step: 1,
    'aria-label': 'Repeat times',
    value: current.count === 'infinite' ? 4 : current.count,
  });
  const infinite = h('input', { type: 'checkbox', checked: current.count === 'infinite' });
  const custom = h('input', { type: 'checkbox', checked: current.period !== null });
  const period = h('input', {
    type: 'text',
    'aria-label': 'Cycle length',
    value: formatTime(current.period ?? repeatPeriod(first) ?? 1000),
  });
  const alternate = h('input', { type: 'checkbox', checked: current.alternate });
  const once = h('input', { type: 'checkbox', checked: first.restartWithScene === false });
  const fields = h(
    'fieldset',
    null,
    h(
      'label',
      { class: 'playback-row' },
      'Times',
      count,
      h('label', { class: 'playback-row' }, infinite, ' Infinite'),
    ),
    h('label', { class: 'playback-row' }, custom, ' Set cycle length', period),
    h('label', { class: 'playback-row' }, alternate, ' Play forwards, then backwards'),
  );

  /** Enables only the controls that apply to the current choices. */
  const sync = (): void => {
    fields.disabled = !repeating.checked;
    count.disabled = infinite.checked;
    period.disabled = !custom.checked;
  };
  for (const control of [repeating, infinite, custom]) control.addEventListener('change', sync);
  sync();

  const apply = h('button', { type: 'button', class: 'primary' }, 'Apply');
  const cancel = h('button', { type: 'button' }, 'Cancel');
  const popover = h(
    'div',
    {
      class: 'popover playback-popover',
      popover: 'auto',
      role: 'dialog',
      'aria-label': 'Track playback',
    },
    h(
      'form',
      { class: 'playback-form' },
      h('label', { class: 'playback-row' }, repeating, ' Repeat this track'),
      fields,
      h(
        'label',
        {
          class: 'playback-row',
          title: 'Plays during the first scene loop, then holds its end pose',
        },
        once,
        " Play once only (don't restart when the scene loops)",
      ),
      h('div', { class: 'playback-actions' }, cancel, apply),
    ),
  );
  document.body.append(popover);
  if (typeof popover.showPopover !== 'function') return;
  popover.showPopover();
  popover.style.left = `${Math.max(8, Math.min(window.innerWidth - popover.offsetWidth - 8, position.x))}px`;
  popover.style.top = `${Math.max(8, Math.min(window.innerHeight - popover.offsetHeight - 8, position.y))}px`;
  popover.addEventListener('toggle', (event) => {
    if (event.newState === 'closed') popover.remove();
  });
  cancel.addEventListener('click', () => {
    popover.hidePopover();
  });
  popover.querySelector('form')?.addEventListener('submit', (event) => {
    event.preventDefault();
  });

  apply.addEventListener('click', () => {
    const parsed = parseTime(period.value);
    const repeat: TrackRepeat | null = repeating.checked
      ? {
          period: custom.checked && parsed !== null ? parsed : null,
          count: infinite.checked
            ? 'infinite'
            : Math.min(1000, Math.max(2, Math.round(Number(count.value) || 2))),
          alternate: alternate.checked,
        }
      : null;
    setTrackPlayback(store, trackIds, { repeat, restartWithScene: !once.checked });
    popover.hidePopover();
  });
  (repeating.checked ? count : repeating).focus();
}
